import i18n from '../../i18n/config';
import {
  agreementColour,
  scoreColourOf,
  toneOf,
  type ResultColour,
  type TeamAgreement,
  type Tone,
} from '../../domain/psychological-safety/team-result';

export type { Tone };
export type ScoreLevel = 'low' | 'medium' | 'good';

export const MAX_SCORE = 5;

const scoreLevels: Record<ResultColour, ScoreLevel> = {
  RED: 'low',
  YELLOW: 'medium',
  GREEN: 'good',
};

/** The level of an average, named for the texts «Lav», «Middels» and «God» */
export const getScoreLevel = (score: number): ScoreLevel => scoreLevels[scoreColourOf(score)];

export const levelTones: Record<ScoreLevel, Tone> = {
  low: toneOf.RED,
  medium: toneOf.YELLOW,
  good: toneOf.GREEN,
};

export const agreementTones: Record<TeamAgreement, Tone> = {
  HIGH: toneOf[agreementColour.HIGH],
  MODERATE: toneOf[agreementColour.MODERATE],
  LOW: toneOf[agreementColour.LOW],
};

const formatters = new Map<string, Intl.NumberFormat>();

/** Formats in the active language: 3,50 in Norwegian, 3.50 in English. */
export const formatScore = (value: number, decimals: 1 | 2 = 1) => {
  const locale = i18n.language === 'en' ? 'en-GB' : 'nb-NO';
  const key = `${locale}-${decimals}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    formatters.set(key, formatter);
  }
  return formatter.format(value);
};

/** +0,2 / −0,2 / ±0,0 with a real minus sign, in the active language */
export const formatChange = (value: number, decimals: 1 | 2 = 1) => {
  const rounded = Number(value.toFixed(decimals));
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '±';
  return `${sign}${formatScore(Math.abs(rounded), decimals)}`;
};

export const changeTone = (value: number): 'success' | 'danger' | 'neutral' =>
  value > 0 ? 'success' : value < 0 ? 'danger' : 'neutral';

export const normalizeQuestion = (text: string) => text.trim().toLowerCase();
